import * as React from 'react';
import { Button, ProgressBar } from 'react-bootstrap';
import notify from '../../shared/feedback/notifications';
import Dropzone from 'dropzone';
import { DropzoneFile, DropzoneOptions } from 'dropzone';
import { Translate } from '../../shared/LanguageService';
import Api from '../../shared/Api';
import { getUserManager } from '../../shared/userManager';

interface IProps {
    actionUrl: string;
    upload?: boolean;
    onFileUploadCompleted?: (file: DropzoneFile) => void;
    onQueueCompleted?: () => void;
    onUploadCompleted?: () => void;
    dropzoneHtmlId?: string;
    maxFiles?: number;
    parallelUploads?: number;
    uploadMultiple?: boolean;
    autoProcessQueue?: boolean;
    maxFilesize?: number;
    addRemoveLinks?: boolean;
    removeFileAfterSuccess?: boolean;
    removeAllFilesAfterQueueCompleted?: boolean;
    accept?: (file: DropzoneFile, done: (error?: string | Error) => void) => void;
    message?: string;
    useProgressbar?: boolean;
    useUploadButton?: boolean;
    acceptedFiles: string;
}

interface IState {
    uploading: boolean;
    uploadDone: boolean;
    uploadSuccess: boolean;
    progressObject: any[];
    uploadError: object;
    totalBytes: number;
    uploadProgress: number;
}

class DropzoneComponent extends React.Component<IProps, IState> {
    private _componentIsMounted: boolean = false;

    static defaultProps = {
        dropzoneHtmlId: 'dropzone',
        maxFiles: undefined,
        parallelUploads: 1,
        uploadMultiple: false,
        autoProcessQueue: false,
        maxFilesize: 100,
        addRemoveLinks: true,
        removeFileAfterSuccess: false
    };

    dropzone: Dropzone;

    constructor(props: IProps) {
        super(props);
        // @ts-ignore
        Dropzone.autoDiscover = false;

        this.state = {
            uploading: false,
            uploadDone: false,
            uploadSuccess: false,
            progressObject: [],
            uploadError: {},
            totalBytes: 0,
            uploadProgress: 0
        };

        this.processQueue = this.processQueue.bind(this);
        this.updateTotalBytes = this.updateTotalBytes.bind(this);
        this.updateErrorForFile = this.updateErrorForFile.bind(this);
    }

    componentDidMount() {
        this._componentIsMounted = true;
        this.dropzone = new Dropzone(`#${this.props.dropzoneHtmlId}`, this.getOptions());
    }

    UNSAFE_componentWillReceiveProps(nextProps: any) {
        if (nextProps.upload && !this.state.uploading) {
            if (this.dropzone.getQueuedFiles().length > 0) {
                this.processQueue();
            } else if (this.props.onUploadCompleted) {
                this.props.onUploadCompleted();
            }
        }
    }

    componentWillUnmount() {
        this._componentIsMounted = false;
    }

    processQueue() {
        this.setState(
            {
                uploadDone: false,
                uploading: true,
                uploadProgress: 0,
                progressObject: []
            } as IState,
            () => {
                this.dropzone.processQueue();
            }
        );
    }

    updateTotalBytes(bytes: number) {
        let totalBytes = this.state.totalBytes + bytes;
        this.setState({ totalBytes: totalBytes } as IState);
    }

    updateErrorForFile(fileName: string) {
        let uploadError = this.state.uploadError;

        if (!uploadError[fileName]) {
            uploadError[fileName] = {};
            uploadError[fileName].nrOfErrors = 1;
        } else {
            uploadError[fileName].nrOfErrors++;
        }

        this.setState({ uploadError: uploadError } as IState);
    }

    getOptions() {
        let component = this;
        let options: DropzoneOptions = {
            acceptedFiles: this.props.acceptedFiles,
            dictDefaultMessage: this.props.message
                ? this.props.message
                : Translate('dropzone/title'),
            dictFallbackMessage: Translate('dropzone/fallback'),
            dictInvalidFileType: Translate('dropzone/invalid-filetype'),
            dictRemoveFile: Translate('dropzone/remove-image'),
            dictCancelUpload: Translate('dropzone/cancel-upload'),
            dictCancelUploadConfirmation: Translate('dropzone/cancel-upload-confirmation'),
            // @ts-ignore
            dictUploadCanceled: Translate('dropzone/upload-canceled'),
            autoProcessQueue: this.props.autoProcessQueue,
            addRemoveLinks: this.props.addRemoveLinks,
            uploadMultiple: this.props.uploadMultiple,
            maxFilesize: this.props.maxFilesize,
            maxFiles: this.props.maxFiles,
            parallelUploads: this.props.parallelUploads,
            previewTemplate: previewTemplate,
            maxThumbnailFilesize: 100,
            createImageThumbnails: true,
            headers: new Api().appendHeaders(),
            init: function () {
                let maxfilesreached = false;

                this.on('addedfile', (file: DropzoneFile) => {
                    component.updateTotalBytes(file.size);
                    file.previewElement.classList.remove('dz-details');
                });

                this.on('maxfilesexceeded', (file: DropzoneFile) => {
                    this.removeAllFiles();
                    this.addFile(file);
                });

                this.on('maxfilesreached', (file: DropzoneFile) => {
                    maxfilesreached = true;
                });

                this.on('queuecomplete', () => {
                    maxfilesreached = false;

                    if (!component.state.uploadSuccess) {
                        return;
                    }

                    if (component.props.onUploadCompleted) {
                        component.props.onUploadCompleted();
                    }

                    if (component.props.removeAllFilesAfterQueueCompleted) {
                        this.removeAllFiles();
                    }

                    if (component._componentIsMounted) {
                        component.setState({
                            uploadDone: true,
                            uploading: false,
                            totalBytes: 0,
                            progressObject: [],
                            uploadProgress: 0
                        } as IState);
                    }

                    if (component.props.onQueueCompleted) {
                        component.props.onQueueCompleted();
                    }

                    this.options.autoProcessQueue = false;
                });

                this.on('complete', (file: DropzoneFile) => {
                    if (component.state.uploadSuccess && component.props.onFileUploadCompleted) {
                        component.props.onFileUploadCompleted(file);
                    }
                });

                this.on('error', (file: DropzoneFile, message: string | Error) => {
                    if (
                        typeof message === 'object' &&
                        message.message === 'Authorization has been denied for this request.'
                    ) {
                        let userManager = getUserManager();
                        userManager.signinSilent().then(() => {
                            this.options.headers = new Api().appendHeaders();
                            this.processFile(file);
                        });
                    }
                    if (!file.accepted) {
                        this.removeFile(file);
                        notify.failure(`${file.name} ${message}`);
                        return;
                    }

                    if (maxfilesreached) {
                        return;
                    }

                    component.updateErrorForFile(file.name);

                    if (
                        !component.state.uploadError[file.name] ||
                        !(component.state.uploadError[file.name].nrOfErrors === 3)
                    ) {
                        setTimeout(() => {
                            this.processFile(file);
                        }, 10000);
                    } else {
                        notify.failure(`File ${file.name} failed to upload`);
                    }
                });

                this.on('success', (file: DropzoneFile) => {
                    if (file.previewElement.classList.contains('dz-error')) {
                        file.previewElement.classList.remove('dz-error');
                    }

                    component.setState({ uploadSuccess: true } as IState);

                    if (component.props.removeFileAfterSuccess) {
                        this.removeFile(file);
                    }
                });

                this.on('processing', () => {
                    this.options.autoProcessQueue = true;
                });

                this.on(
                    'uploadprogress',
                    (file: DropzoneFile, progress: number, bytesSent: number) => {
                        let progressObject = component.state.progressObject;
                        if (!progressObject[file.name]) {
                            progressObject[file.name] = {};
                        }

                        progressObject[file.name] = bytesSent;

                        let totalProgress = 0;
                        for (let element in progressObject) {
                            totalProgress += progressObject[element];
                        }

                        let progressPercentage = parseInt(
                            ((totalProgress / component.state.totalBytes) * 100).toFixed()
                        );
                        component.setState({
                            uploadProgress: Math.min(progressPercentage, 100),
                            progressObject: progressObject
                        } as IState);
                    }
                );

                this.on('removedfile', (file: DropzoneFile) => {
                    component.updateTotalBytes(file.size * -1);
                });
            }
        };

        if (this.props.accept) {
            options.accept = this.props.accept;
        }

        return options;
    }

    render() {
        return (
            <div>
                <form
                    action={this.props.actionUrl}
                    id={this.props.dropzoneHtmlId}
                    className="dropzone dropzone-custom"
                ></form>
                {this.props.useUploadButton && (
                    <Button
                        bsStyle="info"
                        onClick={this.processQueue}
                        disabled={this.state.uploading || this.state.totalBytes === 0}
                    >
                        {!this.state.uploading && <i className="material-icons">cloud_upload</i>}
                        {this.state.uploading && <i className="load-spin-dark"></i>}
                        {Translate('dropzone/button/upload')}
                    </Button>
                )}
                {this.props.useProgressbar && this.state.uploading && (
                    <div className="dropzone-loading editedimages">
                        <ProgressBar
                            className="dropzone-progress"
                            active={this.state.uploadDone}
                            now={this.state.uploadProgress}
                            label={`${this.state.uploadProgress}%`}
                            max={100}
                            bsStyle="success"
                        />
                    </div>
                )}
            </div>
        );
    }
}

export default DropzoneComponent;

export const previewTemplate = `<div class="dz-preview dz-image-preview">
    <div class="dz-image">
        <img data-dz-thumbnail="">
        <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress=""></span></div>
    </div>
    <div class="dz-filename"><span data-dz-name=""></span></div>
    <div class="dz-sizedata">
       <div class="dz-dimensions"></div>
       <div class="dz-size"><span data-dz-size=""></span></div>
    </div>
    <div class="dz-error-message"><span data-dz-errormessage=""></span></div>
    <div class="dz-success-mark">
        <svg width="54px" height="54px" viewBox="0 0 54 54" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" xmlnsSketch="http://www.bohemiancoding.com/sketch/ns">
            <title>Check</title>
            <defs></defs>
            <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" sketchType="MSPage">
                <path d="M23.5,31.8431458 L17.5852419,25.9283877 C16.0248253,24.3679711 13.4910294,24.366835 11.9289322,25.9289322 C10.3700136,27.4878508 10.3665912,30.0234455 11.9283877,31.5852419 L20.4147581,40.0716123 C20.5133999,40.1702541 20.6159315,40.2626649 20.7218615,40.3488435 C22.2835669,41.8725651 24.794234,41.8626202 26.3461564,40.3106978 L43.3106978,23.3461564 C44.8771021,21.7797521 44.8758057,19.2483887 43.3137085,17.6862915 C41.7547899,16.1273729 39.2176035,16.1255422 37.6538436,17.6893022 L23.5,31.8431458 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z"
                    id="Oval-2" stroke-opacity="0.198794158" stroke="#747474" fill-opacity="0.816519475" fill="#FFFFFF" sketchType="MSShapeGroup"></path>
            </g>
        </svg>
    </div>
    <div class="dz-error-mark">
        <svg width="54px" height="54px" viewBox="0 0 54 54" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
            xmlnsSketch="http://www.bohemiancoding.com/sketch/ns">
            <title>Error</title>
            <defs></defs>
            <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" sketchType="MSPage">
                <g id="Check-+-Oval-2" sketchType="MSLayerGroup" stroke="#747474" stroke-opacity="0.198794158" fill="#FFFFFF" fill-opacity="0.816519475">
                    <path d="M32.6568542,29 L38.3106978,23.3461564 C39.8771021,21.7797521 39.8758057,19.2483887 38.3137085,17.6862915 C36.7547899,16.1273729 34.2176035,16.1255422 32.6538436,17.6893022 L27,23.3431458 L21.3461564,17.6893022 C19.7823965,16.1255422 17.2452101,16.1273729 15.6862915,17.6862915 C14.1241943,19.2483887 14.1228979,21.7797521 15.6893022,23.3461564 L21.3431458,29 L15.6893022,34.6538436 C14.1228979,36.2202479 14.1241943,38.7516113 15.6862915,40.3137085 C17.2452101,41.8726271 19.7823965,41.8744578 21.3461564,40.3106978 L27,34.6568542 L32.6538436,40.3106978 C34.2176035,41.8744578 36.7547899,41.8726271 38.3137085,40.3137085 C39.8758057,38.7516113 39.8771021,36.2202479 38.3106978,34.6538436 L32.6568542,29 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z"
                        id="Oval-2" sketchType="MSShapeGroup"></path>
                </g>
            </g>
        </svg>
    </div>
</div>`;
